import { Button, Form, Input, Typography } from 'antd';
import { useCallback, useContext } from 'react';

import { useSetBotTokenMutation } from '@app/api/IntegrationAuth';
import { NotificationContext } from '@app/providers';
import { handleCatchError } from '@app/services';

import {
  AuthStepState,
  type AuthStepInfo,
} from '@pages/Integrations/IntegrationsList/components/ConnectIntegrationModal/types';
import { AuthStepView } from '@pages/Integrations/IntegrationsList/components/ConnectIntegrationModal/ui/AuthStepView';

import type { FC } from 'react';

const { Text, Link } = Typography;
const { Item, useForm } = Form;

export interface BotTokenAuthContentProps {
  integrationId: number | null;
  activeBranchId: number | null;
  stepInfo: AuthStepInfo;
}

interface BotTokenFormValues {
  botToken?: string;
}

const BOT_INSTRUCTION_STEPS = [
  'Откройте @BotFather в Telegram',
  'Создайте бота командой /newbot — или возьмите токен существующего: /mybots → API Token',
  'Вставьте токен сюда',
];

/**
 * Архетип «Токен» — Telegram Bot (task/10730): инструкция BotFather, одно
 * поле токена, проверка сразу (getMe на шлюзе).
 */
export const BotTokenAuthContent: FC<BotTokenAuthContentProps> = ({
  integrationId,
  activeBranchId,
  stepInfo,
}) => {
  const { showNotification } = useContext(NotificationContext);
  const [form] = useForm<BotTokenFormValues>();
  const [setBotToken, { isLoading }] = useSetBotTokenMutation();

  const submit = useCallback(
    async (values: BotTokenFormValues) => {
      if (!integrationId || !activeBranchId || !values.botToken) {
        return;
      }
      try {
        await setBotToken({
          ActiveBranchId: activeBranchId,
          integrationId,
          data: { Value: values.botToken },
        }).unwrap();
      } catch (error) {
        showNotification(handleCatchError(error));
      }
    },
    [integrationId, activeBranchId, setBotToken, showNotification]
  );

  if ([AuthStepState.Success, AuthStepState.Error].includes(stepInfo.state)) {
    return <AuthStepView stepInfo={stepInfo} />;
  }

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={submit}
      className="connect-integration-modal__bot-form"
    >
      {BOT_INSTRUCTION_STEPS.map((stepText, index) => (
        <div
          key={stepText}
          className="connect-integration-modal__instruction-step"
        >
          <span className="connect-integration-modal__instruction-number">
            {index + 1}
          </span>
          {index === 0 ? (
            <Text>
              Откройте{' '}
              <Link
                href="https://t.me/BotFather"
                target="_blank"
                rel="noreferrer"
              >
                @BotFather
              </Link>{' '}
              в Telegram
            </Text>
          ) : (
            <Text>{stepText}</Text>
          )}
        </div>
      ))}
      <Item
        label="Токен бота"
        name="botToken"
        rules={[{ required: true, message: 'Укажите токен бота' }]}
      >
        <Input
          placeholder="1234567890:AA…"
          data-testid="connect-auth-bot-token"
        />
      </Item>
      <Text type="secondary">
        Проверим токен сразу — имя бота подставится в название интеграции.
      </Text>
      <div className="connect-integration-modal__form-actions">
        <Button
          type="primary"
          htmlType="submit"
          loading={isLoading}
          data-testid="connect-auth-set-bot-token"
        >
          Подключить
        </Button>
      </div>
    </Form>
  );
};
